/**
 * One owner, assembled from the two records an owner can have.
 *
 * An owner exists in this system twice over, and the two halves are not kept in
 * sync by anything:
 *
 *   - `lessors` — the BILLING entity. Who the management-fee invoice is made
 *     out to, what apartments hang off them. Created from the Owners page.
 *   - `profiles` + `user_roles` — the PORTAL ACCOUNT. An email that can log in
 *     and see a scoped dashboard. Created from Roles & access.
 *
 * Because the two screens each read only their own half, the same person could
 * be present on one and absent from the other, and nothing surfaced the gap.
 * This module is the join: it takes both lists and returns ONE row per real
 * owner, saying explicitly which halves that owner actually has.
 *
 * It is deliberately pure — no Supabase, no React. The matching rule is the
 * part that has to be right, so it is the part that gets tested.
 */
import type { AdminProfileDirectoryRow } from "@/lib/fetch-admin-profiles-directory";
import { isTestAccount } from "@/lib/is-test-account";
import { normalizeOwnerEmail, type OnboardingStage } from "@/lib/owner-metrics";
import { displayStaffTier } from "@/lib/roles";
import type { Lessor } from "@/types/lessor";

/** Which of the two halves an owner actually has. */
export type OwnerLinkState =
  /** Both: they can log in AND we can invoice them. Nothing to fix. */
  | "linked"
  /** A billing entity with no portal account — we invoice them, they can't log in. */
  | "billing_only"
  /** A portal account with no billing entity — they can log in, we can't invoice them. */
  | "account_only";

/**
 * The action that fills in an owner's missing half, for the row's shortcut.
 *
 * The link state itself is no longer shown as a label (COL-486): which record
 * happens to be missing is a symptom, and the Owners list now filters on what
 * actually happened to the owner instead — see `isLoginPending` and
 * `isOnboardingPending`.
 */
const OWNER_LINK_STATE_FIX_LABEL: Record<OwnerLinkState, string | null> = {
  linked: null,
  billing_only: "Create login",
  account_only: "Add billing entity",
};

/** Translator shape from `useLanguage()`, kept local so this file stays pure. */
type Translate = (key: string, fallback?: string) => string;

/** Translated fix-action label for a link state (null when nothing is missing). */
export function ownerLinkStateFixLabel(
  t: Translate,
  state: OwnerLinkState
): string | null {
  const fixLabel = OWNER_LINK_STATE_FIX_LABEL[state];
  if (!fixLabel) return null;
  return t(`admin.ownerDirectory.linkState.${state}.fixLabel`, fixLabel);
}

/**
 * One owner as the Owners page shows them.
 *
 * `lessor` and `account` are the ORIGINAL records, handed through untouched —
 * every edit writes back to whichever of them it belongs to, so merging here
 * never turns into a second source of truth.
 */
export type OwnerDirectoryRow<L extends Lessor = Lessor> = {
  /**
   * Stable React key AND row identity. Prefixed by side so a lessor id can
   * never collide with a profile uuid.
   */
  key: string;
  /** Lessor name when we have one (it is the billing name), else the account's. */
  displayName: string;
  /** Best known address: the account's if there is one, else the lessor's. */
  email: string | null;
  linkState: OwnerLinkState;
  /** The billing record, or null when this owner only has a login. */
  lessor: L | null;
  /** The portal account, or null when this owner only has a billing record. */
  account: AdminProfileDirectoryRow | null;
};

/** Accounts that count as owners — the same tier rule the role cards use. */
export function isOwnerAccount(account: AdminProfileDirectoryRow): boolean {
  return (
    displayStaffTier((account.user_roles ?? []).map((r) => r.role)) === "owner"
  );
}

/**
 * Does this account explicitly point at this lessor?
 *
 * `property_owners.lessor_id` is the deliberate link an admin creates when they
 * attach an account to a billing entity. It beats the email fallback because it
 * survives an owner changing their email address.
 */
function accountPointsAtLessor(
  account: AdminProfileDirectoryRow,
  lessorId: string
): boolean {
  return (account.property_owners ?? []).some(
    (po) => po.lessor_id != null && String(po.lessor_id) === String(lessorId)
  );
}

/**
 * Merge billing entities and portal accounts into one row per owner.
 *
 * Matching runs in two passes, strongest signal first:
 *   1. the stored `property_owners.lessor_id` link
 *   2. an exact, case-insensitive email match
 *
 * Anything unmatched on either side still gets a row — an owner missing half
 * their record is exactly what this page exists to make visible, so dropping
 * them would defeat the point.
 *
 * Order is preserved: lessors keep the order they arrived in (the list query
 * already sorts them newest-first), and account-only owners follow.
 */
export function mergeOwnerDirectory<L extends Lessor>(
  lessors: readonly L[] | null | undefined,
  accounts: readonly AdminProfileDirectoryRow[] | null | undefined
): OwnerDirectoryRow<L>[] {
  const lessorRows = lessors ?? [];
  const ownerAccounts = (accounts ?? []).filter(isOwnerAccount);

  /** Accounts already spoken for, so one account can't land on two owners. */
  const claimed = new Set<string>();

  // Pass 1 — the explicit lessor_id link.
  const byLessorId = new Map<string, AdminProfileDirectoryRow>();
  for (const lessor of lessorRows) {
    const match = ownerAccounts.find(
      (a) => !claimed.has(a.id) && accountPointsAtLessor(a, String(lessor.id))
    );
    if (match) {
      byLessorId.set(String(lessor.id), match);
      claimed.add(match.id);
    }
  }

  // Pass 2 — email, for the (common) case where nobody ever set the link.
  // Runs only over lessors pass 1 left unmatched, so an explicit link always
  // wins over a coincidental shared address.
  for (const lessor of lessorRows) {
    const id = String(lessor.id);
    if (byLessorId.has(id)) continue;
    const wanted = normalizeOwnerEmail(lessor.email);
    if (wanted == null) continue;
    const match = ownerAccounts.find(
      (a) => !claimed.has(a.id) && normalizeOwnerEmail(a.email) === wanted
    );
    if (match) {
      byLessorId.set(id, match);
      claimed.add(match.id);
    }
  }

  const rows: OwnerDirectoryRow<L>[] = lessorRows.map((lessor) => {
    const account = byLessorId.get(String(lessor.id)) ?? null;
    return {
      key: `lessor:${lessor.id}`,
      // The billing name is the one on the invoice, so it wins when both exist.
      displayName:
        lessor.name?.trim() ||
        account?.name?.trim() ||
        account?.email ||
        "Unnamed owner",
      email: account?.email ?? lessor.email ?? null,
      linkState: account ? "linked" : "billing_only",
      lessor,
      account,
    };
  });

  for (const account of ownerAccounts) {
    if (claimed.has(account.id)) continue;
    rows.push({
      key: `account:${account.id}`,
      displayName: account.name?.trim() || account.email || "Unnamed owner",
      email: account.email ?? null,
      linkState: "account_only",
      lessor: null,
      account,
    });
  }

  return rows;
}

/**
 * The current row for an owner a surface is holding on to (an open profile).
 *
 * Matches on `key` first. A key only changes when the owner's shape does —
 * chiefly an account-only owner (`account:<uid>`) being linked to a billing
 * record (`lessor:<lid>`), which the profile's own "Edit login & access" can
 * do — so it falls back to the same portal account. `undefined` when the owner
 * is gone (deleted, or filtered out); the caller decides what to keep then.
 */
export function findCurrentOwnerRow<L extends Lessor>(
  rows: OwnerDirectoryRow<L>[],
  held: Pick<OwnerDirectoryRow<L>, "key" | "account">
): OwnerDirectoryRow<L> | undefined {
  const byKey = rows.find((r) => r.key === held.key);
  if (byKey) return byKey;
  // Only an account-only owner can legitimately change key. A held `lessor:`
  // key that has vanished means that billing entity is GONE — and falling back
  // on the shared portal account there would silently re-point the open profile
  // at a DIFFERENT billing entity, because one account can be claimed by
  // another lessor (two lessors sharing an email, or a second
  // property_owners.lessor_id link). The caller keeps its snapshot instead.
  if (!held.key.startsWith("account:")) return undefined;
  const accountId = held.account?.id;
  return accountId ? rows.find((r) => r.account?.id === accountId) : undefined;
}

/** Stable id for a portal-account status, independent of its display label. */
export type PortalStatusId =
  | "no_login"
  | "deactivated"
  | "onboarded"
  | "invited"
  | "link_expired"
  | "not_invited";

/** How the owner's portal login is doing, in the words the admin UI uses. */
export type PortalStatus = {
  id: PortalStatusId;
  label: string;
  tone: "emerald" | "sky" | "amber" | "slate";
};

/**
 * The portal-account status pill.
 *
 * Same wording and same precedence as the Roles & access directory used, kept
 * here so the two surfaces cannot drift into calling the same state different
 * things. Deactivation outranks the invitation stage: an onboarded owner whose
 * account was switched off still cannot log in, and saying "Onboarded" there
 * would be a lie.
 */
export function accountPortalStatus(
  account: AdminProfileDirectoryRow | null | undefined
): PortalStatus {
  if (!account) return { id: "no_login", label: "No login", tone: "amber" };
  if (account.is_active === false)
    return { id: "deactivated", label: "Deactivated", tone: "amber" };
  switch (account.onboarding_token_status) {
    case "completed":
      return { id: "onboarded", label: "Onboarded", tone: "emerald" };
    case "pending":
      return { id: "invited", label: "Invited", tone: "sky" };
    case "expired":
      return { id: "link_expired", label: "Link expired", tone: "amber" };
    default:
      return { id: "not_invited", label: "Not invited", tone: "slate" };
  }
}

/** Translated `PortalStatus.label`. */
export function portalStatusLabel(t: Translate, status: PortalStatus): string {
  return t(`admin.ownerDirectory.portalStatus.${status.id}`, status.label);
}

/** A lessor as the Owners list carries it — with its onboarding rollup when enriched. */
type LessorWithStage = Lessor & { onboardingStage?: OnboardingStage | null };

/**
 * CoLiviq's own billing entity (`lessors.is_platform`, at most one row).
 *
 * It legitimately has no owner login and never onboards with itself, so it is
 * carved out of both status filters — otherwise it would sit in them forever
 * and the lists would stop being to-do lists.
 */
export function isPlatformOwner(
  row: OwnerDirectoryRow<LessorWithStage>
): boolean {
  return row.lessor?.is_platform === true;
}

/**
 * The owner cannot log in yet: there is no owner account for them, or the one
 * there is has been deactivated.
 *
 * "Has an account" is the most the client can know — whether they ever signed
 * in is not readable here — so an account that exists and is active counts as
 * done.
 */
export function isLoginPending(
  row: OwnerDirectoryRow<LessorWithStage>
): boolean {
  if (isPlatformOwner(row)) return false;
  return row.account == null || row.account.is_active === false;
}

/**
 * The owner has not finished onboarding: no signed, verified submission yet.
 *
 * With a billing record this is the owner's own onboarding rollup — the same
 * token + verified-submission rule the Status column and the "Onboarded" tile
 * read (`ownerPropertyOnboardingStage`), so the three can never disagree. An
 * owner with no properties has nothing to onboard yet (stage null) and is not
 * counted. An owner with only a login has no properties resolved here, so their
 * latest onboarding token stands in: signing is what marks it `completed`.
 */
export function isOnboardingPending(
  row: OwnerDirectoryRow<LessorWithStage>
): boolean {
  if (isPlatformOwner(row)) return false;
  if (row.lessor) {
    const stage = row.lessor.onboardingStage;
    return stage === "pending" || stage === "invite_sent";
  }
  // Same "nothing to onboard yet" rule as a billing owner with no properties:
  // a login attached to no property has no onboarding to finish.
  if ((row.account?.property_owners ?? []).length === 0) return false;
  return row.account?.onboarding_token_status !== "completed";
}

/** The Owners list status chips. Independent: one owner can be in both. */
export type OwnerStatusFilter = "login_pending" | "onboarding_pending";

export const OWNER_STATUS_FILTERS: readonly OwnerStatusFilter[] = [
  "login_pending",
  "onboarding_pending",
];

const OWNER_STATUS_FILTER_META: Record<
  OwnerStatusFilter,
  {
    label: string;
    hint: string;
    matches: (row: OwnerDirectoryRow<LessorWithStage>) => boolean;
  }
> = {
  login_pending: {
    label: "Login pending",
    hint: "No active owner login yet — they cannot sign in to their dashboard.",
    matches: isLoginPending,
  },
  onboarding_pending: {
    label: "Onboarding pending",
    hint: "Onboarding not completed — not invited yet, or invited but not signed.",
    matches: isOnboardingPending,
  },
};

/** Does this owner belong under the given status chip? */
export function matchesOwnerStatusFilter(
  row: OwnerDirectoryRow<LessorWithStage>,
  filter: OwnerStatusFilter
): boolean {
  return OWNER_STATUS_FILTER_META[filter].matches(row);
}

/** Translated chip label. */
export function ownerStatusFilterLabel(
  t: Translate,
  filter: OwnerStatusFilter
): string {
  return t(
    `admin.ownerDirectory.statusFilter.${filter}.label`,
    OWNER_STATUS_FILTER_META[filter].label
  );
}

/** Translated chip tooltip. */
export function ownerStatusFilterHint(
  t: Translate,
  filter: OwnerStatusFilter
): string {
  return t(
    `admin.ownerDirectory.statusFilter.${filter}.hint`,
    OWNER_STATUS_FILTER_META[filter].hint
  );
}

/** Counts behind the status chips. They overlap, so they need not sum to `all`. */
export function countOwnerStatusFilters(
  rows: readonly OwnerDirectoryRow<LessorWithStage>[]
): Record<OwnerStatusFilter | "all", number> {
  const counts = { all: rows.length, login_pending: 0, onboarding_pending: 0 };
  for (const row of rows)
    for (const filter of OWNER_STATUS_FILTERS)
      if (matchesOwnerStatusFilter(row, filter)) counts[filter] += 1;
  return counts;
}

/**
 * Search across BOTH halves of the row.
 *
 * The list query already filters `lessors` server-side, but an account-only
 * owner never went through that filter — searching in memory here is what keeps
 * those rows findable by name or email.
 */
export function filterOwnerDirectory<L extends Lessor>(
  rows: readonly OwnerDirectoryRow<L>[],
  search: string
): OwnerDirectoryRow<L>[] {
  const q = search.trim().toLowerCase();
  if (!q) return [...rows];
  return rows.filter((row) => {
    const haystack = [
      row.displayName,
      row.email,
      row.lessor?.name,
      row.lessor?.email,
      row.account?.name,
      row.account?.email,
    ];
    return haystack.some(
      (v) => v != null && String(v).toLowerCase().includes(q)
    );
  });
}

/**
 * Is this owner a Test / CoLiviq account — hidden from the Owners page unless
 * its "Include test/CoLiviq accounts" toggle is on (COL-485)?
 *
 * The same email rule Bookings & Tenants and Analytics use (isTestAccount),
 * checked against BOTH halves: the two addresses can differ, and either one
 * being internal marks the owner. Plus the COL-447 demo flag, which Analytics
 * already reads to drop the sales-demo owner's inventory — on this page that
 * owner is the thing the toggle exists to hide.
 */
export function isTestOwner(
  row: Pick<OwnerDirectoryRow, "lessor" | "account">,
  dynamicTestEmails: readonly string[]
): boolean {
  return (
    (row.account?.is_demo_account ?? false) ||
    isTestAccount(row.account?.email, dynamicTestEmails) ||
    isTestAccount(row.lessor?.email, dynamicTestEmails)
  );
}

/**
 * When this owner signed up, as epoch ms, for the newest-first table order.
 *
 * The account is asked first because that IS the signup — a `lessors` row is a
 * billing entity an admin creates, which can predate or outlive the login. An
 * owner with no account at all falls back to when their billing record was
 * made, which is the only date we hold for them.
 *
 * Rows with neither sort last rather than first: an unknown date is not news.
 */
export function ownerSignupTime(
  row: Pick<OwnerDirectoryRow, "lessor" | "account">
): number {
  const stamp = row.account?.created_at ?? row.lessor?.created_at ?? null;
  if (!stamp) return Number.NEGATIVE_INFINITY;
  const ms = Date.parse(stamp);
  return Number.isNaN(ms) ? Number.NEGATIVE_INFINITY : ms;
}

/**
 * Newest signups first (Neel, 2026-10-07).
 *
 * `pinDemoFirst` is the one exception: while "include test/CoLiviq accounts"
 * is ON, the sales-demo owner goes to the top whatever its age, because that
 * is the only reason anyone turns the toggle on. With the toggle OFF the demo
 * owner is filtered out upstream and the flag is moot — but it is passed
 * explicitly rather than inferred, so this stays a pure function.
 *
 * Returns a new array; the caller's input is untouched.
 */
export function sortOwnerDirectory<R extends OwnerDirectoryRow<Lessor>>(
  rows: readonly R[],
  options: { pinDemoFirst?: boolean } = {}
): R[] {
  const { pinDemoFirst = false } = options;
  const isDemo = (r: R) =>
    pinDemoFirst && (r.account?.is_demo_account ?? false);
  return [...rows].sort((a, b) => {
    const demoGap = Number(isDemo(b)) - Number(isDemo(a));
    if (demoGap !== 0) return demoGap;
    // Compared, not subtracted: two undated rows are both -Infinity, and
    // `-Infinity - -Infinity` is NaN, which a comparator must never return —
    // it would skip the tie-break below and leave the order up to the engine.
    const at = ownerSignupTime(a);
    const bt = ownerSignupTime(b);
    if (at !== bt) return bt > at ? 1 : -1;
    // Same instant (seeded rows share a timestamp), or neither dated — keep it
    // deterministic.
    return a.key.localeCompare(b.key);
  });
}
